import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
// Material UI
import { Grid, Typography } from '@mui/material';
// Cloud React components
import { Button, Select } from 'Components';
import SelectLocation from 'Components/Map/components/SelectLocation';
import TableView from 'Components/TableView';
import { NotificationManager } from 'Components/Notifications';
import Directions from './.components/directions';
import EntityPicker from 'Modules/.common/components/EntityPicker';
// Utils
import { TypeLayer } from 'Components/Map/providers/enums';
// Styles
import './nearest.scss';
// Actions
import {
  searchNearest,
  clearRedux,
  openNearestDirectionsModal,
  addPointers,
  centerOnUnit
} from 'Redux/actions';

const NEAREST_LIST_ROWS_PER_PAGE = 20;

const Nearest = (props) => {
  const dispatch = useDispatch();
  const { intl, mapRef } = props;
  const { messages } = intl;

  const nearestFromTypes = [
    {
      id: 'Vehicle', name: messages['nearest_vehicle']
    },
    {
      id: 'Equipment', name: messages['nearest_equipment']
    },
    {
      id: 'Landmark', name: messages['nearest_landmark']
    }
  ];

  const defaultFormData = {
    entity: { id: 'Vehicle', name: messages['nearest_vehicle'] },
    entityType: null,
    from: {}
  };

  const {
    items,
    total,
    error,
    errorMessage,
    loading,
    openDirectionsModal
  } = useSelector(state => state.realtimeMapFindNearestRedux);

  const { user: { isKph }, user } = useSelector((state) => state.securityRedux);

  const [formData, setFormData] = useState({ ...defaultFormData });
  const [formError, setFormError] = useState({});
  const [page, setPage] = useState(0);
  const [showTableView, setShowTableView] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [restart, setRestart] = useState(0);
  const [valueKph, setValueKph] = useState(null)

  useEffect(() => {
    setRestart(restart + 1);
    return () => {
      restarModule()
    }
  }, []);

  useEffect(() => {
    if (error) {
      NotificationManager.error(
        messages['nearest_error'],
        messages['error'],
        3000,
        null,
        null,
        'warning',
      );
      dispatch(clearRedux("FIND_NEAREST_REDUX", { items: [], total: 0, error: false, errorMessage: ""}))
    }
  }, [error, errorMessage]);

  useEffect(() => {
    if(isKph && isKph != valueKph){
      setValueKph(isKph)
    }
  },[isKph])

  useEffect(()=> {
    if(valueKph != null){
      restarModule()
    }
  },[valueKph])

  const setMarkers = useCallback(() => {
    if (selectedItem) {
      highlightItemInMap(selectedItem);
    }
  });

  const restarModule = () => {
    setRestart(restart + 1);
    setFormData({ ...defaultFormData })
    setFormError({})
    setPage(0)
    setShowTableView(false)
    setSelectedItem(null)
    dispatch(addPointers([]));
    dispatch(clearRedux("FIND_NEAREST_REDUX"));
    dispatch(clearRedux("REALTIMEMAPS", { directions: { options: {}, getDirections: false } }));
  }

  const highlightItemInMap = useCallback((item) => {
    const { latitude, longitude, entity } = item;
    if (entity?.name === "Landmark" && mapRef) {
      const coordinates = new google.maps.LatLng(latitude, longitude); // eslint-disable-line no-undef
      const radius = 0;
      const coordinatesCenter = [{ lat: latitude, lng: longitude }];
      mapRef.current.centerByFitBounds(coordinatesCenter, radius)
      const mapObject = {
        id: entity?.id,
        position: coordinates,
        label: entity?.label,
        icon: {
          rotation: 0
        },
        layerName: TypeLayer.Landmark
      }

      mapRef.current.modalPopupEvent(mapObject, mapObject);
    } else if (entity?.name === "Unit") {
      dispatch(centerOnUnit(entity.id));
    } else {
      const marker = {
        id: `nearest_marker_${entity?.id}`,
        lat: latitude,
        lng: longitude,
        label: 'Map Point',
        heading: 0,
        color: '000',
        eventName: 'no event',
        draggable: false
      };

      dispatch(addPointers([marker]));
    }
  })

  const find = useCallback((currentOffset) => {
    dispatch(searchNearest(
      {
        entity: formData?.entity?.id,
        entityType: formData?.entityType?.id,
        from: formData?.from
      },
      NEAREST_LIST_ROWS_PER_PAGE,
      currentOffset
    ));
  });

  const resetSelectedItems = () =>{
    setSelectedItem(null)
  }

  return (
    <div>
      <Typography variant="subtitle1" component="h2">
        {messages['nearest_fromTitle']}
      </Typography>

      <Select
        onChange={(value) => {
          setFormData({ ...formData, entity: value, entityType: null });
        }}
        value={formData.entity}
        id="entity"
        options={nearestFromTypes}
        loading={false}
        isSearchable={false}
        textField="name"
        valueField="id"
        disabled={showTableView}
        required
        requiredMessage={messages['required_field']}
      />

      {
        formData?.entity?.id === "Equipment" &&
        <div>
          <EntityPicker
            id="equipments"
            name="equipments"
            entity="equipments"
            value={formData?.entityType}
            hideLabel
            disabled={showTableView}
            required
            onChange={(value) => {
              setFormData({ ...formData, entityType: value });
              setFormError({});
            }}
          />
          {
            formError?.entityType &&
            <small className="text-danger" style={{ paddingTop: 10 }}>
              {formError?.entityType}
            </small>
          }
        </div>
      }

      <Typography variant="subtitle1" component="h2" style={{ marginTop: 15 }}>
        {messages['nearest_toTitle']}
      </Typography>

      <div>
        <SelectLocation
          disabled={showTableView}
          mapRef={mapRef}
          showInfoBubble
          restart={restart}
          onChange={(value) => {
            const { location, type, data } = value;
            let label = "";
            let from = "";
            let entityId = "";

            if (type?.value === "unit") {
              from = "Unit";
              entityId = data?.id;
              label = data?.label;
            } else if (type?.value === "landmark") {
              from = "Landmark";
              entityId = location?.landmarkId;
              label = data?.name;
            } else if (type?.value === "address" || type?.value === "click") {
              from = "Coordinate";
            }

            setFormData({
              ...formData,
              from: {
                from: from,
                coordinate: {
                  latitude: location?.latitude,
                  longitude: location?.longitude
                },
                entityId: entityId
              },
              name: `${(label ? `(${label})` : "")} ${location?.address}`
            });
            setFormError({});
          }}
        />

        {
          formError?.from &&
          <small className="text-danger" style={{ paddingTop: 10 }}>
            {formError?.from}
          </small>
        }
      </div>

      {
        !showTableView &&
        <div style={{ textAlign: 'right', marginTop: 15 }}>
          <Button
            className="btn-blue"
            disabled={false}
            onClick={(e) => {
              if (e) e.preventDefault();

              if (formData?.entity?.id === 'Equipment' && !formData?.entityType?.id) {
                setFormError({ entityType: messages['required_field'] });
              } else {
                if (!formData?.from?.from || !formData?.from?.coordinate || !formData?.from?.coordinate?.latitude || !formData?.from?.coordinate?.longitude) {
                  setFormError({ from: messages['required_field'] });
                } else {
                  setShowTableView(true);
                  setPage(0);
                }
              }
            }}
          >
            {messages['nearest_find']}
          </Button>
        </div>
      }

      <hr className="nearest-separator" />

      {
        showTableView &&
        <div>
          <div style={{ textAlign: 'right', padding: '10px 0px 10px 0px' }}>
            <Button
              type="span"
              style={{ textDecoration: 'none' }}
              disabled={loading}
              onClick={(e) => {
                if (e) e.preventDefault();
                setShowTableView(false);
                dispatch(clearRedux("FIND_NEAREST_REDUX"));
                dispatch(addPointers([]));
                dispatch(clearRedux("REALTIMEMAPS", { directions: { options: {}, getDirections: false } }));
                setRestart(restart + 1)
                setSelectedItem(null);
                setFormError({});
              }}
            >
              {messages['nearest_clear']}
            </Button>
          </div>

          <TableView
            data={items}
            loading={loading}
            rowsPerPage={NEAREST_LIST_ROWS_PER_PAGE}
            name="nearest-items-list"
            page={page + 1}
            height={'50vh'}
            infiniteScroll={true}
            viewType="list"
            messages={messages}
            itemSelector={false}
            serverSide
            onChangePage={(page) => {
              const offset = (page - 1) * NEAREST_LIST_ROWS_PER_PAGE;
              find(offset);
            }}
            total={total}
            moreRowsAvailable={items.length < total}
            renderRow={(row) => {
              return (
                <div className="nearest-items-container">
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    wrap="nowrap"
                  >
                    <Grid item xs zeroMinWidth>
                      <Typography noWrap>{row?.entity?.label || messages['notAvailable']} </Typography>
                    </Grid>
                    <Grid item>
                      <Typography color="textSecondary">{(isKph ? row?.distanceMeters / 1000 : row?.distanceMeters / 1609.344).toFixed(1)} {messages[ isKph ? 'nearest_kilometers' : 'nearest_miles']}</Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        type="icon"
                        icon="room"
                        className={`${selectedItem?.entity?.id === row?.entity?.id ? 'selected-item' : ''}`}
                        tooltip={messages['nearest_show']}
                        onClick={(e) => {
                          if (e) e.preventDefault();
                          setSelectedItem(row);
                          highlightItemInMap(row);
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        type="icon"
                        icon="directions"
                        tooltip={messages['nearest_directions']}
                        onClick={(e) => {
                          if (e) e.preventDefault();
                          const { from } = formData;
                          const { coordinate } = from;

                          mapRef?.current?.hideMarkerModal();

                          const points = [
                            {
                              id: `find_nearest_1`,
                              lat: row?.latitude,
                              lng: row?.longitude,
                              label: 'Map Point',
                              name: row?.entity?.label,
                              heading: 0,
                              color: '000',
                              eventName: 'no event',
                              draggable: true
                            },
                            {
                              id: `find_nearest_2`,
                              lat: coordinate?.latitude,
                              lng: coordinate?.longitude,
                              label: 'Map Point',
                              name: formData.name,
                              heading: 0,
                              color: '000',
                              eventName: 'no event',
                              draggable: true
                            }
                          ];

                          dispatch(openNearestDirectionsModal(true, points));
                        }}
                      />
                    </Grid>
                  </Grid>
                </div>
              )
            }}
          />
        </div>
      }

      {openDirectionsModal && <Directions mapRef={mapRef} onClose={setMarkers} resetSelectedItems={resetSelectedItems} />}
    </div>
  )
}

export default injectIntl(Nearest);